const cities = [
  { name: "Joplin", zips: "64801, 64802, 64803, 64804" },
  { name: "Webb City", zips: "64870" },
  { name: "Carthage", zips: "64836" },
  { name: "Carl Junction", zips: "64834" },
  { name: "Neosho", zips: "64850" },
  { name: "Pittsburg, KS", zips: "66762" },
  { name: "Miami, OK", zips: "74354" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home or office in Joplin, Webb City, or Carthage in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping from the Joplin UPS or FedEx to send documents directly — we handle every detail.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function JoplinLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Joplin &amp; the Tri-State Region
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Joplin is an economic crossroads that few travelers fully appreciate until they need a passport fast. At the convergence of Missouri, Kansas, and Oklahoma — the famous Tri-State area — the city has long served as a regional commercial hub for industries that routinely conduct international business. Mining, manufacturing, healthcare, retail, and agriculture all draw Joplin-area residents into international travel patterns that standard government passport processing simply cannot accommodate on typical corporate or operational timelines.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The city's Route 66 heritage brings a steady stream of domestic and international visitors who occasionally discover passport emergencies here — a stolen wallet, a lost travel document before the next leg of a road trip that crosses into Canada or Mexico. But the more persistent demand comes from the local business community. Freeman Health System and Mercy Hospital Joplin draw international medical professionals and patients. Downstream-affiliated industrial operations, cattle ranching operations with export business, and regional manufacturing companies all generate business travelers who find their passport expired at the worst possible moment.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Missouri Southern State University adds another layer of travel urgency. Study-abroad programs, international student travel, and faculty exchange programs create cyclical bursts of first-time passport applications that collide with semester deadlines. Students often discover they need a passport after acceptance into a program — when standard government processing leaves them scrambling to find an expedited solution.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              The most frustrating reality for Joplin-area travelers is geographic: the nearest Regional Passport Agency is over three hours away in St. Louis. Getting an appointment there is never guaranteed. Fast Passport Center was built for exactly this market — travelers in mid-size cities far from passport agencies who need professional, reliable expedited processing without a multi-hour round trip. Start online, ship overnight from the Joplin FedEx or UPS, and we handle the rest.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Joplin, MO</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Joplin &amp; Tri-State Region</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Serving Jasper County, Newton County &amp; the Tri-State border region
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Serving Joplin, Webb City, Carthage, Carl Junction, and Neosho</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start your order online, then <strong className="text-white">ship with our prepaid label</strong> from the Joplin UPS or FedEx, or schedule a drop-off. Most clients ship overnight — no drive to St. Louis required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the Tri-State region — start online, ship overnight, and we handle the rest. No drive to St. Louis required.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Missouri Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Springfield", href: "/springfield-mo" },
                    { name: "Kansas City", href: "/kansas-city" },
                    { name: "St. Louis", href: "/st-louis" },
                    { name: "Columbia", href: "/columbia-mo" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Joplin Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}